import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { addressLabelList } from 'constants/order';
import { status } from 'constants/general';
import orderStrings from 'modules/order/strings';
import GoogleMaps from 'modules/common/components/GoogleMaps';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  FormWrapper,
  Title,
  SubTitle,
  InputLabel,
  Input,
  ValidationErrMsg,
  SingleItemSelectWrapper,
  SingleItemSelect,
  SubmitBtn,
} from './AddAddressComponent.style';

const AddAddressComponent = ({
  formFieldsValues,
  formFieldsValidation,
  selectedLocation,
  handleTextInputFieldChange,
  handleAddressLabelFieldChange,
  handleGoogleMapsLocationChange,
  handleSubmitBtnClick,
}) => {
  const { addAddressInfo } = useSelector(state => ({
    addAddressInfo: state.order.addAddressInfo,
  }));

  return (
    <Wrapper>
      <FormWrapper>
        <Title>{orderStrings.addAddressForm.formTitle}</Title>
        <SectionDivider gap={4} />
        <SubTitle>{orderStrings.addAddressForm.formSubTitle}</SubTitle>
        <SectionDivider gap={45} />
        {/* Street Field */}
        <InputLabel>{orderStrings.addAddressForm.formStreetFieldLabel}</InputLabel>
        <SectionDivider gap={4} />
        <Input
          value={formFieldsValues.street}
          placeholder={orderStrings.addAddressForm.formStreetFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('street', event)}
        />
        {formFieldsValidation.street === false && (
          <ValidationErrMsg>
            {orderStrings.addAddressForm.formFieldErrorText(
              orderStrings.addAddressForm.formStreetFieldLabel,
            )}
          </ValidationErrMsg>
        )}
        <SectionDivider gap={15} />

        {/* Building Field */}
        <InputLabel>{orderStrings.addAddressForm.formBuildingFieldLabel}</InputLabel>
        <SectionDivider gap={4} />
        <Input
          value={formFieldsValues.building}
          placeholder={orderStrings.addAddressForm.formBuildingFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('building', event)}
        />
        {formFieldsValidation.building === false && (
          <ValidationErrMsg>
            {orderStrings.addAddressForm.formFieldErrorText(
              orderStrings.addAddressForm.formBuildingFieldLabel,
            )}
          </ValidationErrMsg>
        )}
        <SectionDivider gap={15} />

        {/* Flat Field */}
        <InputLabel>{orderStrings.addAddressForm.formFlatFieldLabel}</InputLabel>
        <SectionDivider gap={4} />
        <Input
          value={formFieldsValues.flat}
          placeholder={orderStrings.addAddressForm.formFlatFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('flat', event)}
        />
        {formFieldsValidation.flat === false && (
          <ValidationErrMsg>
            {orderStrings.addAddressForm.formFieldErrorText(
              orderStrings.addAddressForm.formFlatFieldLabel,
            )}
          </ValidationErrMsg>
        )}
        <SectionDivider gap={15} />

        {/* Landmark Field */}
        <InputLabel>{orderStrings.addAddressForm.formLandmarkFieldLabel}</InputLabel>
        <SectionDivider gap={4} />
        <Input
          value={formFieldsValues.landmark}
          placeholder={orderStrings.addAddressForm.formLandmarkFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('landmark', event)}
        />
        <SectionDivider gap={15} />

        {/* Label Field */}
        <InputLabel>{orderStrings.addAddressForm.formLabelFieldLabel}</InputLabel>
        <SectionDivider gap={4} />
        <SingleItemSelectWrapper>
          {addressLabelList.map(addressLabel => (
            <SingleItemSelect
              key={addressLabel.key}
              selected={formFieldsValues.label === addressLabel.key}
              onClick={() => handleAddressLabelFieldChange(addressLabel.key)}
            >
              {addressLabel.name}
            </SingleItemSelect>
          ))}
        </SingleItemSelectWrapper>
      </FormWrapper>
      <SectionDivider gap={30} />

      {/* Maps Field */}
      <GoogleMaps
        selectedLocation={selectedLocation}
        handleLocationChange={handleGoogleMapsLocationChange}
      />
      <SectionDivider gap={35} />
      {(addAddressInfo.status === status.FETCHING && (
        <Loader width={30} height={30} thickness={5} />
      )) || (
        <SubmitBtn as="button" onClick={() => handleSubmitBtnClick()}>
          {orderStrings.addAddressForm.formSubmitBtn}
        </SubmitBtn>
      )}
    </Wrapper>
  );
};

AddAddressComponent.propTypes = {
  formFieldsValues: PropTypes.shape({
    street: PropTypes.string,
    building: PropTypes.string,
    flat: PropTypes.string,
    landmark: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    street: PropTypes.bool,
    building: PropTypes.bool,
    flat: PropTypes.bool,
    landmark: PropTypes.bool,
    label: PropTypes.bool,
  }).isRequired,
  selectedLocation: PropTypes.shape({}).isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleAddressLabelFieldChange: PropTypes.func.isRequired,
  handleGoogleMapsLocationChange: PropTypes.func.isRequired,
  handleSubmitBtnClick: PropTypes.func.isRequired,
};

export default AddAddressComponent;
